import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, Box, Typography, Button, IconButton, Stack,
    Checkbox, FormControlLabel, Link
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExtensionIcon from '@mui/icons-material/Extension';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { EXTENSION_STORE_URL } from '../utils/extensionBridge';

/**
 * Asked before a Track click that the assistant would not be able to act on.
 *
 * Tracking without the extension is still worth doing — the email alerts are
 * the free half of the product — so this asks and never refuses. "Track
 * anyway" is always there and always tracks.
 *
 * Two states, one dialog:
 *
 * - **Not installed.** The add-to-cart assistant lives in the extension, so
 *   without it a restock is an email rather than a cart.
 * - **Installed but not connected.** The button connects, onetapapply's
 *   pattern (#234): the page hands the extension its own token and the
 *   extension verifies it with the API before answering, so a click either
 *   tracks straight away or learns that the API refused the page's token.
 *   Only that second case sends anyone back through sign-in. The old
 *   "Sign in again" button assumed a stale "not connected" meant a dead
 *   session; it usually meant a stale flag, and signing in never cleared it.
 */

const BENEFITS = [
    'Opens the product and adds to cart the moment stock drops',
    'You review the cart and check out yourself',
    'Email alerts keep working either way'
];

export const TrackGateDialog = ({ open, installed, connecting = false, onTrackAnyway, onPrimary, onClose }) => {
    const [dontShowAgain, setDontShowAgain] = useState(false);

    useEffect(() => {
        if (open) {
            setDontShowAgain(false);
        }
    }, [open]);

    const title = installed
        ? 'Connect the extension first?'
        : 'Let the extension cart this one';

    const body = installed
        ? 'The extension is installed but is not connected to your StockMaid account yet, so it '
            + 'cannot add this to your cart when it restocks. Connecting takes one click.'
        : 'Our Chrome extension watches this product and adds it to your Best Buy cart the moment '
            + 'it comes back in stock. Without it, tracking still emails you — you just add to the '
            + 'cart yourself.';

    return (
        <Dialog
            open={open}
            onClose={() => onClose(dontShowAgain)}
            maxWidth="xs"
            fullWidth
            data-testid="track-gate"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: 'hidden',
                    maxWidth: 480,
                    width: 'calc(100% - 16px)',
                    bgcolor: 'var(--background-color-light)',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundImage: 'none',
                    position: 'relative',
                    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)'
                }
            }}
        >
            <IconButton
                onClick={() => onClose(dontShowAgain)}
                aria-label="Close"
                disableRipple
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: 10,
                    color: 'text.primary',
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    p: 0,
                    '&:hover': { bgcolor: 'var(--background-color-dark)' },
                    zIndex: 1
                }}
            >
                <CloseIcon sx={{ fontSize: 26 }} />
            </IconButton>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: 4, pt: 5, pb: 4 }}>
                    <Typography
                        variant="h6"
                        sx={{ mb: 2, fontWeight: 700, color: 'text.primary' }}
                    >
                        {title}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                        <ExtensionIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                    </Box>

                    <Typography sx={{ mb: 2.5, color: 'text.primary' }}>
                        {body}
                    </Typography>

                    <Stack spacing={1} sx={{ mb: 2 }}>
                        {BENEFITS.map((line) => (
                            <Stack key={line} direction="row" spacing={1} alignItems="flex-start">
                                <CheckCircleOutlineIcon
                                    fontSize="small"
                                    sx={{ mt: 0.25, color: 'text.secondary' }}
                                />
                                <Typography variant="body2" color="text.secondary">{line}</Typography>
                            </Stack>
                        ))}
                    </Stack>

                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={dontShowAgain}
                                onChange={(event) => setDontShowAgain(event.target.checked)}
                                size="small"
                            />
                        )}
                        label={(
                            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                                Don't show this again
                            </Typography>
                        )}
                        sx={{ mb: 2 }}
                    />

                    {installed ? (
                        <Button
                            onClick={() => onPrimary(dontShowAgain)}
                            variant="contained"
                            fullWidth
                            disabled={connecting}
                            data-testid="track-gate-primary"
                            sx={{
                                bgcolor: 'var(--sm-btn-contained-bg)',
                                color: 'var(--sm-btn-contained-text)',
                                py: 1.4,
                                borderRadius: 2,
                                fontWeight: 'bold',
                                '&:hover': { bgcolor: 'var(--sm-btn-contained-hover)' }
                            }}
                        >
                            {connecting ? 'Connecting…' : 'Connect the extension'}
                        </Button>
                    ) : (
                        <Button
                            component="a"
                            href={EXTENSION_STORE_URL}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => onPrimary(dontShowAgain)}
                            variant="contained"
                            fullWidth
                            data-testid="track-gate-primary"
                            sx={{
                                bgcolor: 'var(--sm-btn-contained-bg)',
                                color: 'var(--sm-btn-contained-text)',
                                py: 1.4,
                                borderRadius: 2,
                                fontWeight: 'bold',
                                '&:hover': { bgcolor: 'var(--sm-btn-contained-hover)' }
                            }}
                        >
                            Get the Chrome Extension
                        </Button>
                    )}

                    {/* A link, not a second button: the way out, not the recommendation. */}
                    <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                        <Link
                            component="button"
                            type="button"
                            onClick={() => onTrackAnyway(dontShowAgain)}
                            data-testid="track-anyway"
                            color="text.secondary"
                            variant="body2"
                        >
                            Track anyway
                        </Link>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default TrackGateDialog;
